import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import PrivateRoute from './PrivateRoute';

import { Login } from '../pages/Login';
import { MainMenu } from '../pages/MainMenu';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { RecursosHumanos } from '../global/rrhh/RecursosHumanos';
import { Calendario } from '../global/calendario/Calendario';
import { Admin } from '../global/administrador/Admin';
import { Usuarios } from '../global/administrador/usuarios/Usuarios';
import { Notificaciones } from '../global/administrador/notificaciones/Notificaciones';
import { Header } from '../global/Header';
import { RolesYDepartamentos } from '../global/administrador/permisos/RolesYDepartamentos';
import { Footer } from '../global/Footer';
import { LateralMenu } from '../global/LateralMenu';
import { Grid } from '@mui/material';
import { BtnSubir } from '../utils/BtnSubir';
import { Contabilidad } from '../global/contabilidad/Contabilidad';
import { CrearGasto } from '../global/contabilidad/CrearGasto';
import { Comidas } from '../global/comidas/Comidas';
import { Ropa } from '../global/ropa/Ropa';
import { TablonAnuncios } from '../global/tablonAnuncios/TablonAnuncios';
import { AccesoRapidoFormAsustencia } from '../global/calendario/AccesoRapidoFormAsustencia';
import { Actividades } from '../global/actividades/Actividades';
import { Eventos } from '../global/eventos/Eventos';
import { Fototeca } from '../global/fototeca/Fototeca';
import { getCookie } from '../utils/cookies';
import { getMarcaCliente } from '../services/apis';
import { Configuracion } from '../global/configuracion/Configuracion';
import { MisUsuarios } from '../global/misUsuarios/MisUsuarios';
import { MiUsuario } from '../global/miUsuario/MiUsuario';

const DEFAULT_BRAND = {
	primary: '#030813',
	logo: '',
	nombre: 'Penya App',
	publicKey: '',
	logoVersion: 0,
};
const DEFAULT_SECONDARY = '#545f72';
const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ''}/favicon.ico`;
const DEFAULT_MANIFEST = `${process.env.PUBLIC_URL || ''}/manifest.json`;
const WS_BASE_URL = 'https://www.elpelobetxi.com/WS/';

const normalizeColor = (color, fallback) => {
	if (typeof color !== 'string') {
		return fallback;
	}

	const trimmedColor = color.trim();
	return /^#[0-9A-Fa-f]{6}$/.test(trimmedColor) ? trimmedColor : fallback;
};

const updateBrowserBrand = ({ nombre, logo, publicKey, logoVersion }) => {
	document.title = nombre || DEFAULT_BRAND.nombre;

	let favicon = document.querySelector('link[rel="icon"]');
	if (!favicon) {
		favicon = document.createElement('link');
		favicon.rel = 'icon';
		document.head.appendChild(favicon);
	}

	favicon.href = logo || DEFAULT_FAVICON;

	let manifest = document.querySelector('link[rel="manifest"]');
	if (!manifest) {
		manifest = document.createElement('link');
		manifest.rel = 'manifest';
		document.head.appendChild(manifest);
	}

	manifest.crossOrigin = 'anonymous';
	manifest.href = publicKey
		? `${WS_BASE_URL}manifestCliente.php?k=${encodeURIComponent(publicKey)}&v=${encodeURIComponent(logoVersion || 0)}`
		: DEFAULT_MANIFEST;
};

const createAppTheme = (brand) => createTheme({
	palette: {
		mode: 'light',
		primary: {
			main: brand.primary,
			contrastText: '#ffffff',
		},
		secondary: {
			main: DEFAULT_SECONDARY,
			contrastText: '#ffffff',
		},
		error: {
			main: '#ba1a1a',
			contrastText: '#ffffff',
		},
		background: {
			default: '#f7fafc',
			paper: '#ffffff',
		},
		text: {
			primary: '#181c1e',
			secondary: '#45474c',
		},
		divider: '#edf2f7',
		surface: {
			main: '#f7fafc',
			container: '#ebeef0',
			low: '#f1f4f6',
		},
	},
	shape: {
		borderRadius: 4,
	},
	typography: {
		fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
		h1: {
			fontFamily: '"Hanken Grotesk", Inter, sans-serif',
			fontSize: '48px',
			fontWeight: 700,
			lineHeight: '56px',
			letterSpacing: '-0.02em',
		},
		h4: {
			fontFamily: '"Hanken Grotesk", Inter, sans-serif',
			fontSize: '32px',
			fontWeight: 600,
			lineHeight: '40px',
			letterSpacing: '-0.01em',
		},
		h5: {
			fontFamily: '"Hanken Grotesk", Inter, sans-serif',
			fontSize: '24px',
			fontWeight: 600,
			lineHeight: '32px',
		},
		h6: {
			fontFamily: 'Inter, sans-serif',
			fontSize: '18px',
			fontWeight: 600,
			lineHeight: '28px',
		},
		body1: {
			fontSize: '16px',
			lineHeight: '24px',
		},
		body2: {
			fontSize: '14px',
			lineHeight: '20px',
		},
		button: {
			fontSize: '12px',
			fontWeight: 600,
			lineHeight: '16px',
			letterSpacing: '0.05em',
			textTransform: 'none',
		},
	},
	components: {
		MuiButton: {
			defaultProps: {
				disableElevation: true,
			},
			styleOverrides: {
				root: {
					borderRadius: 4,
					fontWeight: 600,
				},
				outlined: {
					backgroundColor: '#ffffff',
					borderColor: '#c6c6cc',
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					border: '1px solid #edf2f7',
					boxShadow: 'none',
					backgroundImage: 'none',
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: 'none',
				},
				elevation1: {
					boxShadow: 'none',
					border: '1px solid #edf2f7',
				},
				elevation2: {
					boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
				},
				elevation3: {
					boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				variant: 'outlined',
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: 4,
					backgroundColor: '#ffffff',
					'& fieldset': {
						borderColor: '#edf2f7',
					},
					'&:hover fieldset': {
						borderColor: '#c6c6cc',
					},
					'&.Mui-focused fieldset': {
						borderColor: brand.primary,
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					borderBottomColor: '#edf2f7',
				},
				head: {
					color: '#45474c',
					fontWeight: 600,
					backgroundColor: '#f1f4f6',
				},
			},
		},
		MuiDialog: {
			styleOverrides: {
				paper: {
					borderRadius: 8,
					boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
				},
			},
		},
		MuiDrawer: {
			styleOverrides: {
				paper: {
					backgroundColor: '#f1f4f6',
					borderRight: '1px solid #edf2f7',
				},
			},
		},
	},
});

const Router = () => {
	//global
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [brand, setBrand] = useState(DEFAULT_BRAND);
	const [openDrawer, setOpenDrawer] = useState(
		localStorage.getItem('openDrawer') === 'true'
	);
	const theme = useMemo(() => createAppTheme(brand), [brand]);
	const cargarMarcaCliente = useCallback(async () => {
		if (!getCookie('PHPSESSID')) {
			setBrand(DEFAULT_BRAND);
			return;
		}

		const response = await getMarcaCliente();
		const cliente = response?.success ? response.data : null;
		const primary = normalizeColor(cliente?.color_principal, DEFAULT_BRAND.primary);

		setBrand({
			primary,
			logo: cliente?.logo || '',
			nombre: cliente?.nombre_mostrar || DEFAULT_BRAND.nombre,
			publicKey: cliente?.public_key || '',
			logoVersion: cliente?.logo_version || 0,
		});
	}, []);

	useEffect(() => {
		localStorage.setItem('openDrawer', openDrawer);
	}, [openDrawer]);

	useEffect(() => {
		cargarMarcaCliente();
		window.addEventListener('penya-auth-changed', cargarMarcaCliente);

		return () => {
			window.removeEventListener('penya-auth-changed', cargarMarcaCliente);
		};
	}, [cargarMarcaCliente]);

	useEffect(() => {
		const root = document.documentElement;
		root.style.setProperty('--ki-primary', brand.primary);
		root.style.setProperty('--ki-primary-container', brand.primary);
		root.style.setProperty('--ki-secondary', DEFAULT_SECONDARY);
		root.style.setProperty('--principal', brand.primary);
		root.style.setProperty('--secundario', DEFAULT_SECONDARY);
		updateBrowserBrand(brand);
	}, [brand]);


	return (
		<ThemeProvider theme={theme}>
			<BrowserRouter>
				<div className='back-main fade-in'>
					<Grid container direction={'column'}
						size={{ xs: 12 }}>
						<Grid container direction={'row'}
							size={{ xs: 12 }}
							sx={{display: getCookie('PHPSESSID') ? 'flex' : 'none'}}>
							<Header setOpenDrawer={setOpenDrawer} openDrawer={openDrawer} logoCliente={brand.logo} />
						</Grid>
						<Grid container direction={'row'}
							size={{ xs: 12 }}>
							<Grid container
								size={{ xs: 12, lg: 2 }}>
								{openDrawer && (
									<LateralMenu setOpenDrawer={setOpenDrawer} openDrawer={openDrawer} nombreCliente={brand.nombre}></LateralMenu>
								)}
							</Grid>
							<Grid
								container
								sx={{ pb: 10 }}
								size={{
									xs: 12,
									lg: getCookie('PHPSESSID')
										? openDrawer ? 10 : 12
										: 12
								}}
							>
								<Grid container direction={'row'}
									size={{ xs: 12 }}>
									<Grid container direction={'column'}
										size={{ xs: 12 }}>
										<Routes>
											<Route element={<PrivateRoute />}>
												<Route path="/" element={<MainMenu />}></Route>
												{/* //tareas */}
												{/* //recursos humanos */}
												<Route path="/rrhh" element={<RecursosHumanos />}></Route>

												{/* //calendario */}
												<Route path="/calendario" element={<Calendario />}></Route>
												{/* //admin */}
												<Route path="/admin" element={<Admin />}></Route>
												{/* //usuarios */}
												<Route path="/usuarios" element={<Usuarios />}></Route>
												<Route path="/mis-usuarios" element={<MisUsuarios />}></Route>
												<Route path="/mi-usuario" element={<MiUsuario />}></Route>
												{/* //departamentos */}
												<Route path="/rolesydepartamentos" element={<RolesYDepartamentos />}></Route>
												{/* //notificaciones */}
												<Route path="/notificaciones" element={<Notificaciones />}></Route>
												{/* contabilidad */}
												<Route path="/contabilidad" element={<Contabilidad />}></Route>
												<Route path="/despesas/nova" element={<CrearGasto />}></Route>
												{/* comidas */}
												<Route path="/comidas" element={<Comidas />} />
												{/* samarretes */}
												<Route path="/samarretes" element={<Ropa />} />
												<Route path="/fototeca" element={<Fototeca />} />
												<Route path="/configuracion" element={<Configuracion />} />

												{/* //tablon de anuncios */}
												<Route path="/tauler" element={<TablonAnuncios />} />
												{/* //lector de tickets */}
												<Route path="/accesorapidocomida/:id" element={<AccesoRapidoFormAsustencia />} ></Route>
												<Route path="/activitats" element={<Actividades />} />
												<Route path="/esdeveniments" element={<Eventos />} />
												<Route path="/accesorapidolimpieza/:id" element={<AccesoRapidoFormAsustencia />} ></Route>
												<Route path="/accesorapidoactividad/:id" element={<AccesoRapidoFormAsustencia />} ></Route>


											</Route>
											{/* //global */}
											<Route path="/login?" element={<Login username={username} setUsername={setUsername} password={password} setPassword={setPassword} />}></Route>
											<Route path="*" element={<Navigate to="/" replace />} />
										</Routes>
										<BtnSubir></BtnSubir>
										<Footer></Footer>
									</Grid>

								</Grid>
							</Grid>

						</Grid>
					</Grid>
				</div>
			</BrowserRouter>
		</ThemeProvider >
	)
}

export default Router
